import { c as _c } from "react/compiler-runtime";
import type { MatchLevel } from '@onefootprint/request-types/dashboard';
import { Card, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CardTitle, { CARD_STATUS_TEXT, CardStatus, type CardStatusTone } from '../../card-title';
import useMatchSignalLabels from '../hooks/use-match-signal-labels';
import type { TransformedMatchSignalDataType } from '../utils/transform-response';
const LEVEL_TONE: Record<MatchLevel, CardStatusTone> = {
  no_match: 'error',
  could_not_match: 'warning',
  partial: 'warning',
  exact: 'success'
};

// A document can carry 15+ matched crosschecks; a few show that they ran without burying what failed.
const COLLAPSED_MATCHED_COUNT = 3;
// Collapsing only pays off when it hides a few reasons.
const MIN_HIDDEN_COUNT = 3;
type ValidationCardProps = {
  validation: TransformedMatchSignalDataType;
};
const ValidationCard = t0 => {
  const $ = _c(40);
  const {
    validation: t1
  } = t0;
  const {
    attribute,
    matchLevel,
    signals
  } = t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "onboardings.validations"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t2);
  const {
    getAttributeLabel,
    getMatchLevelLabel
  } = useMatchSignalLabels();
  const [isExpanded, setIsExpanded] = useState(false);
  const listId = useId();
  let t3;
  if ($[1] !== attribute || $[2] !== getAttributeLabel) {
    t3 = getAttributeLabel(attribute);
    $[1] = attribute;
    $[2] = getAttributeLabel;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const title = t3;
  let t4;
  if ($[4] !== signals) {
    t4 = signals.filter(_temp);
    $[4] = signals;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const matchedCount = t4.length;
  const hiddenCount = matchedCount - COLLAPSED_MATCHED_COUNT;
  const isCollapsible = hiddenCount >= MIN_HIDDEN_COUNT;
  let t5;
  if ($[6] !== hiddenCount || $[7] !== isCollapsible || $[8] !== isExpanded || $[9] !== signals) {
    t5 = isCollapsible && !isExpanded ? signals.slice(0, signals.length - hiddenCount) : signals;
    $[6] = hiddenCount;
    $[7] = isCollapsible;
    $[8] = isExpanded;
    $[9] = signals;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const shownSignals = t5;
  const t6 = LEVEL_TONE[matchLevel];
  let t7;
  if ($[11] !== getMatchLevelLabel || $[12] !== matchLevel) {
    t7 = getMatchLevelLabel(matchLevel);
    $[11] = getMatchLevelLabel;
    $[12] = matchLevel;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <CardStatus tone={t6}>{t7}</CardStatus>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8 || $[18] !== title) {
    t9 = <CardTitle title={title} rightContent={t8} />;
    $[17] = t8;
    $[18] = title;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== getMatchLevelLabel || $[21] !== listId || $[22] !== matchLevel || $[23] !== shownSignals || $[24] !== signals.length || $[25] !== t || $[26] !== title) {
    t10 = signals.length ? <ul id={listId} aria-label={title} className="flex flex-col gap-1.5">{shownSignals.map(signal_0 => <li key={signal_0.reasonCode} className="flex gap-2 text-body-2 text-primary"><span aria-hidden={true} className="flex h-5 shrink-0 items-center"><span className={cx("size-1.5 rounded-full bg-current", CARD_STATUS_TEXT[LEVEL_TONE[signal_0.matchLevel]])} /></span><span>{signal_0.matchLevel !== matchLevel && <span className="sr-only">{`${getMatchLevelLabel(signal_0.matchLevel)}: `}</span>}{signal_0.description}</span></li>)}</ul> : <p className="text-body-2 text-tertiary">{t(_temp2)}</p>;
    $[20] = getMatchLevelLabel;
    $[21] = listId;
    $[22] = matchLevel;
    $[23] = shownSignals;
    $[24] = signals.length;
    $[25] = t;
    $[26] = title;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== hiddenCount || $[29] !== isCollapsible || $[30] !== isExpanded || $[31] !== listId || $[32] !== t) {
    t11 = isCollapsible && <LinkButton className="mt-2 self-start" aria-expanded={isExpanded} aria-controls={listId} onClick={() => setIsExpanded(!isExpanded)}>{isExpanded ? t(_temp3) : t(_temp4, {
        count: hiddenCount
      })}</LinkButton>;
    $[28] = hiddenCount;
    $[29] = isCollapsible;
    $[30] = isExpanded;
    $[31] = listId;
    $[32] = t;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11) {
    t12 = <Card.Body className="flex flex-1 flex-col px-4 py-2.75">{t10}{t11}</Card.Body>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t12 || $[38] !== t9) {
    t13 = <Card.Root className="flex flex-col">{t9}{t12}</Card.Root>;
    $[37] = t12;
    $[38] = t9;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
};
export default ValidationCard;
function _temp(signal) {
  return signal.matchLevel === "exact";
}
function _temp2($) {
  return $.noDetails;
}
function _temp3($_0) {
  return $_0.showFewer;
}
function _temp4($_1) {
  return $_1.showMatched;
}